import { useTranslation } from 'react-i18next'
import {
  KnowledgeAskCitation,
  KnowledgeDocumentListItem,
} from '@digital-twins/shared-types'
import { Heading } from '@/components/ui/Heading'
import {
  CitationDocumentGroup,
  referencesSidebarTitle,
} from './groupCitationsByDocument'
import { ThreadSourceCard } from './ThreadSourceCard'

/**
 * What the sources drawer shows when no single source is open: one card per
 * cited document, for the newest grounded answer.
 *
 * Content only. The shell around it — width, border, dock-or-cover, the open
 * animation — belongs to ThreadSourcesDrawer, which the opened-source view
 * shares, so following a citation reads as looking closer inside the same pane
 * rather than swapping it for another one.
 */

interface ThreadReferencesSidebarProps {
  groups: Array<CitationDocumentGroup>
  docsByToken: Map<string, KnowledgeDocumentListItem>
  resolveLabel: (citation: KnowledgeAskCitation) => string
  onOpenCitation: (index: number, citation: KnowledgeAskCitation) => void
}

export function ThreadReferencesSidebar(props: ThreadReferencesSidebarProps) {
  const { groups, docsByToken, resolveLabel, onOpenCitation } = props

  const { t } = useTranslation('app')

  if (groups.length === 0) {
    return null
  }

  return (
    <div
      className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      data-testid="thread-references-sidebar"
    >
      <div className="px-4 pb-2 pt-4">
        <Heading level={2} size="sm" weight="medium">
          {referencesSidebarTitle(groups.length)}
        </Heading>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 pb-4">
        {groups.map((group) => {
          const first = group.citations[0]?.citation
          const doc =
            (first?.documentToken
              ? docsByToken.get(first.documentToken)
              : undefined) ??
            (first?.documentName
              ? docsByToken.get(first.documentName)
              : undefined) ??
            null
          return (
            <ThreadSourceCard
              key={group.key}
              group={group}
              document={doc}
              title={first ? resolveLabel(first) : t('library.facet.source')}
              onOpenCitation={onOpenCitation}
            />
          )
        })}
      </div>
    </div>
  )
}
